import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  FormText,
} from 'reactstrap';
import { Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useBuybackData from '../common/useBuybackData';
import useBuybackFeedback from '../common/useBuybackFeedback';
import useUserInvestments from '../common/useUserInvestments';
import ErrorNotification from '../../../shared/components/ErrorNotification';
import checkApiErrorResponse from '../../../shared/services/checkApiErrorResponse';
import BUYBACK_CONFIG, {
  getBuybackSubmissionWindow,
  isBuybackPageWindowActive,
  isBuybackSubmissionWindowActive,
} from './config';

const BUYBACK_UNIT_PRICE_USD = BUYBACK_CONFIG.unitPriceUsd;
const BUYBACK_MIN_TOTAL_VALUE_USD = BUYBACK_CONFIG.minParticipationUsd;

const PRO_RATA_2X_LABEL = 'Sell Up to 2× Pro-Rata';
const PRO_RATA_2X_LABEL_WITH_FOOTNOTE = `${PRO_RATA_2X_LABEL}†`;
const PRO_RATA_2X_FOOTNOTE = '† Sell Up to 2× Pro-Rata elections are theoretical and subject to availability.';

const RESPONSE_TYPE_LABELS = {
  DECLINE: 'Pass (0)',
  PRO_RATA: 'Sell 1× Pro-Rata',
  PRO_RATA_2X: PRO_RATA_2X_LABEL_WITH_FOOTNOTE,
};

const RESPONSE_TYPE_OPTIONS = [
  { value: 'DECLINE', label: RESPONSE_TYPE_LABELS.DECLINE },
  { value: 'PRO_RATA', label: RESPONSE_TYPE_LABELS.PRO_RATA },
  { value: 'PRO_RATA_2X', label: RESPONSE_TYPE_LABELS.PRO_RATA_2X },
];

const MIN_FONT_SIZE_STYLE = { fontSize: '14px', lineHeight: 1.4 };

const RESPONSE_TYPE_TO_SELECTION = {
  DECLINE: 'HOLD',
  PRO_RATA: 'PRO_RATA',
  PRO_RATA_2X: 'PRO_RATA_2X',
};

const SELECTION_TO_RESPONSE_TYPE = {
  HOLD: 'DECLINE',
  PRO_RATA: 'PRO_RATA',
  PRO_RATA_2X: 'PRO_RATA_2X',
};

const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const currencyFormatterWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatNumberWithSeparators = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'number') {
    return numberFormatter.format(value);
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value.replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(numeric)) {
      return numberFormatter.format(numeric);
    }
    return value;
  }

  return '—';
};

const parseNumericValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    if (cleaned.trim() === '') {
      return null;
    }
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const normalizeUnitsValue = (value) => {
  const parsed = parseNumericValue(value);
  if (parsed === null || Number.isNaN(parsed)) {
    return null;
  }
  return Math.max(0, Math.floor(parsed));
};

const getMaximumUnitsForInput = (input) => {
  if (!input) {
    return null;
  }

  const candidates = [
    input.maximumProfitUnits,
    input.maximumAvailable,
    input.numberOfUnits,
    input.shares,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const value = normalizeUnitsValue(candidates[index]);
    if (value !== null) {
      return value;
    }
  }

  return null;
};

const computeUnitsForSelection = (input, selection) => {
  if (!input || !selection) {
    return null;
  }

  if (selection === 'HOLD') {
    return 0;
  }

  if (selection === 'PRO_RATA') {
    return normalizeUnitsValue(input.proRataUnits);
  }

  if (selection === 'PRO_RATA_2X') {
    return normalizeUnitsValue(input.proRataUnits2x);
  }

  return null;
};

const determineSelectionFromUnits = (input, requestedUnits) => {
  const numericUnits = normalizeUnitsValue(requestedUnits);

  if (numericUnits === null) {
    return null;
  }

  if (numericUnits === 0) {
    return 'HOLD';
  }

  const matchesUnits = (value) => {
    const candidate = normalizeUnitsValue(value);
    if (candidate === null) {
      return false;
    }
    return Math.abs(candidate - numericUnits) <= 1;
  };

  if (matchesUnits(input.proRataUnits)) {
    return 'PRO_RATA';
  }

  if (matchesUnits(input.proRataUnits2x)) {
    return 'PRO_RATA_2X';
  }

  return null;
};

const getSelectionAmount = (input, selection, getMinimum = false) => {
  if (!input || !selection || selection === 'HOLD') {
    return null;
  }

  let amount = null;
  if (selection === 'PRO_RATA' || (selection === 'PRO_RATA_2X' && getMinimum === true)) {
    amount = parseNumericValue(input.proRataDollarAmount);
  } else if (selection === 'PRO_RATA_2X') {
    amount = parseNumericValue(input.proRataDollarAmount2x);
  }

  if (amount === null || Number.isNaN(amount)) {
    const fallbackUnits = normalizeUnitsValue(input.requestedUnits);
    if (fallbackUnits !== null) {
      return fallbackUnits * BUYBACK_UNIT_PRICE_USD;
    }
    return null;
  }

  return amount;
};

const updateInputWithSelection = (input, selection) => {
  if (!input) {
    return input;
  }

  if (!selection) {
    return {
      ...input,
      selection: '',
      requestedUnits: null,
    };
  }

  const requestedUnits = computeUnitsForSelection(input, selection);

  return {
    ...input,
    selection,
    requestedUnits,
  };
};

const getSelectionLabel = (selection) => {
  if (!selection) {
    return '';
  }

  if (selection === 'HOLD') {
    return RESPONSE_TYPE_LABELS.DECLINE;
  }

  if (selection === 'PRO_RATA') {
    return RESPONSE_TYPE_LABELS.PRO_RATA;
  }

  if (selection === 'PRO_RATA_2X') {
    return RESPONSE_TYPE_LABELS.PRO_RATA_2X;
  }

  return selection;
};

const formatCurrency = (value) => {
  const numeric = parseNumericValue(value);
  if (numeric === null) {
    return '—';
  }
  return currencyFormatter.format(numeric);
};

const formatCurrencyWhole = (value) => {
  const numeric = parseNumericValue(value);
  if (numeric === null) {
    return '—';
  }
  return currencyFormatterWhole.format(numeric);
};

const formatUnitsLabel = (value) => {
  const numeric = normalizeUnitsValue(value);
  if (numeric === null) {
    return null;
  }
  return `${formatNumberWithSeparators(numeric)} units`;
};

const formatValuationDate = (userInvestments) => {
  if (!Array.isArray(userInvestments) || !Array.isArray(userInvestments[0])) {
    return null;
  }

  const endDateEntry = userInvestments[0].find(
    (entry) => entry && entry.key === 'EndDate' && entry.value,
  );

  if (!endDateEntry || typeof endDateEntry.value !== 'object') {
    return null;
  }

  const { month, day, year } = endDateEntry.value;
  if (!month || !day || !year) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateOnly = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const sortLotsByAcquisitionDateAscending = (lots = []) => [...lots].sort((a, b) => {
  const aDate = a && a.acquisitionDate ? new Date(a.acquisitionDate).getTime() : Infinity;
  const bDate = b && b.acquisitionDate ? new Date(b.acquisitionDate).getTime() : Infinity;

  if (Number.isNaN(aDate) && Number.isNaN(bDate)) {
    return 0;
  }

  if (Number.isNaN(aDate)) {
    return 1;
  }

  if (Number.isNaN(bDate)) {
    return -1;
  }

  return aDate - bDate;
});

const formatWindowRange = (start, end) => {
  const startLabel = start ? formatDateTime(start) : null;
  const endLabel = end ? formatDateTime(end) : null;

  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }

  if (startLabel) {
    return startLabel;
  }

  if (endLabel) {
    return endLabel;
  }

  return null;
};

const getSellRequestKey = (sellRequest, index) => {
  if (sellRequest && sellRequest.sellRequestId) {
    return sellRequest.sellRequestId;
  }

  const segments = [
    sellRequest?.netsEntityId,
    sellRequest?.shareClass,
    sellRequest?.sharePrice,
  ]
    .map((segment) => {
      if (segment === null || segment === undefined) {
        return null;
      }
      const value = String(segment).trim();
      return value.length ? value : null;
    })
    .filter(Boolean);

  if (segments.length) {
    return segments.join('_');
  }

  if (sellRequest?.entityName) {
    return String(sellRequest.entityName);
  }

  return `sell-request-${index}`;
};

const getErrorMessage = (error) => {
  if (!error) {
    return 'Something went wrong. Please try again.';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (Array.isArray(error) && error.length > 0) {
    return getErrorMessage(error[0]);
  }

  if (error.message) {
    return error.message;
  }

  if (error.statusText) {
    return error.statusText;
  }

  if (error.statusCode && error.error) {
    return `${error.statusCode}: ${error.error}`;
  }

  return 'Something went wrong. Please try again.';
};

const extractStatusCode = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const code = extractStatusCode(value[index]);
      if (code) {
        return code;
      }
    }
    return null;
  }

  if (typeof value === 'object') {
    if (typeof value.statusCode === 'number') {
      return value.statusCode;
    }
    if (typeof value.status === 'number') {
      return value.status;
    }
    if (value.response) {
      return extractStatusCode(value.response);
    }
  }

  return null;
};

const isForbiddenResponse = (value) => extractStatusCode(value) === 403;

const Buyback = () => {
  const buybackFeatureEnabled = BUYBACK_CONFIG.enableFeature && isBuybackPageWindowActive();
  const buybackSubmissionEnabled = buybackFeatureEnabled && isBuybackSubmissionWindowActive();
  const submissionWindowBounds = getBuybackSubmissionWindow();
  const submissionWindowRangeLabel = formatWindowRange(
    submissionWindowBounds.start,
    submissionWindowBounds.end,
  );
  const now = new Date();
  const isBeforeSubmissionWindow = Boolean(
    submissionWindowBounds.start && now < submissionWindowBounds.start,
  );
  const isAfterSubmissionWindow = Boolean(
    submissionWindowBounds.end && now > submissionWindowBounds.end,
  );
  const submissionStatusMessage = buybackSubmissionEnabled
    ? null
    : (() => {
      if (isBeforeSubmissionWindow && submissionWindowBounds.start) {
        const startLabel = formatDateTime(submissionWindowBounds.start);
        if (startLabel) {
          return `The submission window opens on ${startLabel}.`;
        }
      }

      if (isAfterSubmissionWindow && submissionWindowBounds.end) {
        const endLabel = formatDateTime(submissionWindowBounds.end);
        if (endLabel) {
          return `The submission window closed on ${endLabel}.`;
        }
      }

      if (submissionWindowRangeLabel) {
        return `Submissions are only accepted between ${submissionWindowRangeLabel}.`;
      }

      return 'Submissions are not being accepted at this time.';
    })();
  const showSubmissionWindowRange = !buybackSubmissionEnabled
    && Boolean(submissionWindowRangeLabel)
    && (isBeforeSubmissionWindow || isAfterSubmissionWindow);

  if (!buybackFeatureEnabled) {
    return <Redirect to="/" />;
  }

  const currentManagedUser = useSelector((state) => state.currentManagedUser);
  const auth = useSelector((state) => state.auth);
  const userQuerySuffix = currentManagedUser ? `?userid=${currentManagedUser}` : '';
  const buybackUnits = useBuybackData(userQuerySuffix);
  const {
    feedback,
    isLoading: isFeedbackLoading,
    error: feedbackError,
    submit,
    isSubmitting,
    submitError,
    clearSubmitError,
  } = useBuybackFeedback(
    userQuerySuffix,
    { enabled: buybackFeatureEnabled },
  );
  const userInvestments = useUserInvestments(userQuerySuffix);

  const valuationDate = useMemo(
    () => formatValuationDate(userInvestments),
    [userInvestments],
  );

  const isBuybackLoading = buybackUnits === null;
  const buybackRequestErrored = buybackUnits
    ? checkApiErrorResponse(buybackUnits)
    : false;
  const hasBuybackError = buybackRequestErrored;
  const hasBuybackData = Array.isArray(buybackUnits)
    && buybackUnits.length > 0
    && !hasBuybackError;
  const buybackUnitsForbidden = !Array.isArray(buybackUnits) && isForbiddenResponse(buybackUnits);
  const feedbackForbidden = isForbiddenResponse(feedbackError);
  const submitForbidden = isForbiddenResponse(submitError);
  const unauthorizedAccess = buybackUnitsForbidden || feedbackForbidden || submitForbidden;

  const [responseType, setResponseType] = useState('');
  const [sellRequestInputs, setSellRequestInputs] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    sellRequests: {},
    form: '',
  });
  const [submissionSuccessMessage, setSubmissionSuccessMessage] = useState('');
  const feedbackSectionRef = useRef(null);

  useEffect(() => {
    if (!sellRequestInputs || sellRequestInputs.length === 0) {
      setResponseType('');
      return;
    }

    const selections = sellRequestInputs
      .map((input) => (input ? input.selection : null))
      .filter((selection) => selection);

    if (selections.length === 0) {
      setResponseType('');
      return;
    }

    const uniqueSelections = new Set(selections);

    if (uniqueSelections.size === 1) {
      const [selection] = Array.from(uniqueSelections);
      const mapped = SELECTION_TO_RESPONSE_TYPE[selection] || '';
      setResponseType(mapped);
      return;
    }

    setResponseType('');
  }, [sellRequestInputs]);

  const userPhone = useMemo(() => (
    auth?.user?.mobilePhone || ''
  ), [auth?.user?.mobilePhone]);

  const userAddress = useMemo(() => {
    const user = auth?.user;
    if (!user || !user.address1) {
      return 'No address on file';
    }
    const parts = [
      user.address1,
      user.address2,
      user.city,
      user.state,
      user.postalCode,
      user.country,
    ].filter(Boolean);
    return parts.join(', ');
  }, [auth?.user?.address1, auth?.user?.address2, auth?.user?.city, auth?.user?.state, auth?.user?.postalCode, auth?.user?.country]);

  const buybackPriceDisplay = formatCurrencyWhole(BUYBACK_UNIT_PRICE_USD);
  const minParticipationDisplay = formatCurrencyWhole(BUYBACK_MIN_TOTAL_VALUE_USD);
  const poolCapDisplay = formatCurrencyWhole(BUYBACK_CONFIG.poolCapUsd);
  const summaryStats = (() => {
    const stats = [
      { label: 'Total Buyback Amount', value: poolCapDisplay },
      { label: 'Current Price per Unit', value: buybackPriceDisplay },
      { label: 'Minimum Participation', value: minParticipationDisplay },
    ];
    const maximumParticipation = BUYBACK_CONFIG.summary?.maximumParticipation;
    if (maximumParticipation) {
      stats.push({
        label: 'Maximum Participation',
        value: maximumParticipation,
      });
    }
    return stats;
  })();
  const timelineStats = [
    { label: 'Participation Deadline', value: BUYBACK_CONFIG.summary.deadline },
    { label: 'Settlement Date', value: BUYBACK_CONFIG.summary.settlementDate },
  ];
  const BUYBACK_CAMPAIGN_KEY = BUYBACK_CONFIG.campaignKey || 'buyback2025';

  const buildSellRequestInputs = useCallback(
    (existingSellRequests = []) => {
      if (!Array.isArray(buybackUnits)) {
        return [];
      }

      const existingMap = new Map(
        existingSellRequests
          .filter((request) => request && request.sellRequestId)
          .map((request) => [request.sellRequestId, request]),
      );

      let sellRequestSequence = 0;

      return buybackUnits.map((entity, entityIndex) => {
        if (!entity || typeof entity !== 'object') {
          return null;
        }

        const { netsEntityId } = entity;
        const { entityName } = entity;
        const shareClasses = Array.isArray(entity.shareClasses) ? entity.shareClasses : [];

        const normalizedChildren = shareClasses.map((shareClass) => {
          if (!shareClass || typeof shareClass !== 'object') {
            return null;
          }

          sellRequestSequence += 1;
          const normalizedShares = normalizeUnitsValue(shareClass.shares);
          const catchupPrice = parseNumericValue(shareClass.catchupPricePerShare);

          const baseChild = {
            sellRequestId: getSellRequestKey({
              sellRequestId: shareClass.sellRequestId,
              netsEntityId,
              shareClass: shareClass.shareClass,
              sharePrice: shareClass.sharePrice,
              entityName,
            }, sellRequestSequence),
            entityName,
            netsEntityId,
            shareClass: shareClass.shareClass,
            sharePrice: shareClass.sharePrice,
            acquisitionDate: shareClass.acquisitionDate || null,
            numberOfUnits: normalizedShares,
            shares: normalizedShares,
            maximumAvailable: normalizedShares,
            maximumProfitUnits: normalizedShares,
            catchupPricePerShare: catchupPrice,
          };

          const existing = existingMap.get(baseChild.sellRequestId);
          const units = normalizeUnitsValue(existing?.requestedNumberOfUnitsToSell);
          baseChild.requestedUnits = units;
          baseChild.requestedTenderValue = units ? units * BUYBACK_UNIT_PRICE_USD : null;

          return baseChild;
        }).filter(Boolean);

        const sortedChildren = normalizedChildren.sort((a, b) => {
          const aPrice = parseNumericValue(a.sharePrice);
          const bPrice = parseNumericValue(b.sharePrice);
          if (aPrice === null && bPrice === null) {
            return 0;
          }
          if (aPrice === null) {
            return 1;
          }
          if (bPrice === null) {
            return -1;
          }
          return aPrice - bPrice;
        });

        let totalExistingUnits = 0;
        const childSellRequests = sortedChildren.map((child) => {
          const clone = { ...child };
          if (clone.requestedUnits) {
            totalExistingUnits += clone.requestedUnits;
          }
          return clone;
        });

        const normalizedTotalUnits = normalizeUnitsValue(entity.totalUnitsVested);
        const aggregated = {
          sellRequestId: `entity-${netsEntityId || entityIndex}`,
          entityName,
          netsEntityId,
          sharePrice: childSellRequests[0]?.sharePrice ?? null,
          childSellRequests,
          shares: normalizedTotalUnits,
          numberOfUnits: normalizedTotalUnits,
          maximumAvailable: normalizedTotalUnits,
          maximumProfitUnits: normalizedTotalUnits,
          proRataUnits: normalizeUnitsValue(entity.proRata1xUnits),
          proRataUnits2x: normalizeUnitsValue(entity.proRata2xUnits),
          proRataDollarAmount: parseNumericValue(entity.proRata1xDollars),
          proRataDollarAmount2x: parseNumericValue(entity.proRata2xDollars),
          selection: '',
          requestedUnits: null,
        };

        const aggregatedExisting = existingMap.get(aggregated.sellRequestId);
        const storedSelection = aggregatedExisting && typeof aggregatedExisting.selection === 'string'
          ? aggregatedExisting.selection.trim()
          : null;
        const storedUnits = aggregatedExisting
          ? normalizeUnitsValue(aggregatedExisting.requestedNumberOfUnitsToSell)
          : null;

        if (storedUnits !== null) {
          totalExistingUnits = storedUnits;
          aggregated.requestedUnits = storedUnits;
        }

        if (storedSelection) {
          aggregated.selection = storedSelection;
        }

        if (totalExistingUnits > 0) {
          const inferredSelection = determineSelectionFromUnits(aggregated, totalExistingUnits);
          const resolvedSelection = inferredSelection || storedSelection || aggregated.selection;
          if (resolvedSelection) {
            const updated = updateInputWithSelection(aggregated, resolvedSelection);
            updated.requestedUnits = totalExistingUnits;
            return updated;
          }
          return {
            ...aggregated,
            requestedUnits: totalExistingUnits,
          };
        }

        if (storedSelection) {
          const updated = updateInputWithSelection(aggregated, storedSelection);
          if (storedUnits !== null) {
            updated.requestedUnits = storedUnits;
          }
          return updated;
        }

        return aggregated;
      }).filter(Boolean);
    },
    [buybackUnits],
  );

  const resetValidationState = useCallback(() => {
    setValidationErrors({
      sellRequests: {},
      form: '',
    });
    if (submitError) {
      clearSubmitError();
    }
  }, [submitError, clearSubmitError]);

  useEffect(() => {
    if (
      !hasBuybackData
      || !Array.isArray(buybackUnits)
      || buybackUnits.length === 0
    ) {
      return;
    }

    const existingSellRequests = feedback && Array.isArray(feedback.sellRequests)
      ? feedback.sellRequests
      : [];

    const initialSelection = feedback && feedback.responseType
      ? RESPONSE_TYPE_TO_SELECTION[feedback.responseType] || ''
      : '';

    const preparedInputs = buildSellRequestInputs(existingSellRequests).map((input) => {
      if (!input) {
        return input;
      }

      if (input.selection) {
        return updateInputWithSelection(input, input.selection);
      }

      if (initialSelection) {
        return updateInputWithSelection(input, initialSelection);
      }

      return input;
    });

    setSellRequestInputs(preparedInputs);
    if (!feedback) {
      resetValidationState();
    }
  }, [
    buildSellRequestInputs,
    feedback,
    hasBuybackData,
    buybackUnits,
    resetValidationState,
  ]);

  const handleResponseTypeChange = useCallback((event) => {
    const { value } = event.target;
    setResponseType(value);
    setValidationErrors((prev) => ({
      ...prev,
      responseType: '',
      sellRequests: {},
      form: '',
    }));

    const selection = RESPONSE_TYPE_TO_SELECTION[value];
    if (!selection) {
      return;
    }

    setSellRequestInputs((prev) => prev.map((input) => {
      if (!input) {
        return input;
      }
      return updateInputWithSelection(input, selection);
    }));
  }, []);

  useEffect(() => {
    if (!isFeedbackLoading && feedback && feedbackSectionRef.current) {
      feedbackSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isFeedbackLoading, feedback]);

  const handleSellRequestSelectionChange = useCallback((sellRequestId, selection) => {
    setSellRequestInputs((prev) => prev.map((input) => {
      if (!input) {
        return input;
      }

      if (input.sellRequestId !== sellRequestId) {
        return input;
      }

      return updateInputWithSelection(input, selection);
    }));

    setValidationErrors((prev) => {
      const updatedSellRequests = { ...prev.sellRequests };
      updatedSellRequests[sellRequestId] = '';
      return {
        ...prev,
        sellRequests: updatedSellRequests,
        form: '',
      };
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {
      responseType: '',
      sellRequests: {},
      form: '',
    };
    let hasErrors = false;
    let totalRequestedValue = 0;

    sellRequestInputs.forEach((input) => {
      if (!input) {
        return;
      }

      const {
        sellRequestId,
        selection,
        requestedUnits,
      } = input;

      if (!selection) {
        errors.sellRequests[sellRequestId] = 'Select an option.';
        hasErrors = true;
        return;
      }

      if (selection === 'HOLD') {
        return;
      }

      if (requestedUnits === null || requestedUnits === undefined || Number.isNaN(requestedUnits)) {
        errors.sellRequests[sellRequestId] = 'This option is unavailable for this position.';
        hasErrors = true;
        return;
      }

      if (requestedUnits < 0) {
        errors.sellRequests[sellRequestId] = 'Units cannot be negative.';
        hasErrors = true;
        return;
      }

      const maxUnits = getMaximumUnitsForInput(input);
      if (maxUnits !== null && requestedUnits > maxUnits) {
        errors.sellRequests[sellRequestId] = `Cannot exceed ${formatNumberWithSeparators(maxUnits)} units.`;
        hasErrors = true;
        return;
      }

      totalRequestedValue += requestedUnits * BUYBACK_UNIT_PRICE_USD;
    });

    if (totalRequestedValue > 0 && totalRequestedValue < BUYBACK_MIN_TOTAL_VALUE_USD) {
      errors.form = `Minimum participation is ${minParticipationDisplay}. Current total is ${formatCurrency(totalRequestedValue)}. You may still submit below the minimum, but allocations will prioritize responses that meet or exceed it.`;
    }

    setValidationErrors(errors);
    return !hasErrors;
  }, [sellRequestInputs, minParticipationDisplay]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!buybackSubmissionEnabled) {
        return;
      }

      setSubmissionSuccessMessage('');
      resetValidationState();

      if (!validateForm()) {
        return;
      }

      const normalizedRequests = sellRequestInputs
        .filter((input) => input && input.selection)
        .map((input) => {
          const totalUnits = normalizeUnitsValue(input.requestedUnits);
          return {
            sellRequestId: input.sellRequestId,
            netsEntityId: input.netsEntityId,
            entityName: input.entityName,
            selection: input.selection,
            requestedNumberOfUnitsToSell: totalUnits !== null && totalUnits !== undefined ? Math.max(totalUnits, 0) : 0,
          };
        });

      const payload = {
        campaignKey: BUYBACK_CAMPAIGN_KEY,
        sellRequests: normalizedRequests,
      };

      const response = await submit(payload);

      if (response) {
        setSubmissionSuccessMessage('Your buyback election has been recorded. We will follow up with next steps soon.');
      }
    },
    [
      buybackSubmissionEnabled,
      resetValidationState,
      sellRequestInputs,
      submit,
      validateForm,
    ],
  );

  const sortedSellRequestInputs = useMemo(() => {
    if (!Array.isArray(sellRequestInputs)) {
      return [];
    }

    return [...sellRequestInputs].sort((a, b) => {
      const aPrice = parseNumericValue(a?.sharePrice);
      const bPrice = parseNumericValue(b?.sharePrice);

      if (aPrice === null && bPrice === null) {
        return 0;
      }
      if (aPrice === null) {
        return 1;
      }
      if (bPrice === null) {
        return -1;
      }
      return aPrice - bPrice;
    });
  }, [sellRequestInputs]);

  const formTotalTenderValue = useMemo(() => (
    Array.isArray(sellRequestInputs)
      ? sellRequestInputs.reduce((sum, input) => {
        if (!input || !input.selection || input.selection === 'HOLD') {
          return sum;
        }
        const quantity = normalizeUnitsValue(input.requestedUnits);
        if (quantity === null) {
          return sum;
        }
        return sum + (quantity * BUYBACK_UNIT_PRICE_USD);
      }, 0)
      : 0
  ), [sellRequestInputs]);

  const formTotalSelectedUnits = useMemo(() => (
    Array.isArray(sellRequestInputs)
      ? sellRequestInputs.reduce((sum, input) => {
        if (!input || !input.selection || input.selection === 'HOLD') {
          return sum;
        }
        const quantity = normalizeUnitsValue(input.requestedUnits);
        if (quantity === null) {
          return sum;
        }
        return sum + quantity;
      }, 0)
      : 0
  ), [sellRequestInputs]);

  const formTotalSelectedAmount = useMemo(() => (
    Array.isArray(sellRequestInputs)
      ? sellRequestInputs.reduce((sum, input) => {
        if (!input || !input.selection || input.selection === 'HOLD') {
          return sum;
        }
        const amount = getSelectionAmount(input, input.selection);
        if (amount === null || Number.isNaN(amount)) {
          return sum;
        }
        return sum + amount;
      }, 0)
      : 0
  ), [sellRequestInputs]);

  const formTotalMinAmount = useMemo(() => (
    Array.isArray(sellRequestInputs)
      ? sellRequestInputs.reduce((sum, input) => {
        if (!input || !input.selection || input.selection === 'HOLD') {
          return sum;
        }
        const amount = getSelectionAmount(input, input.selection, true);
        if (amount === null || Number.isNaN(amount)) {
          return sum;
        }
        return sum + amount;
      }, 0)
      : 0
  ), [sellRequestInputs]);

  const hasProRata2xSelection = useMemo(() => (
    sellRequestInputs.some((input) => input && input.selection === 'PRO_RATA_2X')
  ), [sellRequestInputs]);

  const showElectionSelectors = buybackSubmissionEnabled;

  const proceedsHeading = 'Theoretical proceeds';
  const proceedsDescription = 'Based on your current selections. Subject to availability.';

  const isBelowMinimum = formTotalTenderValue > 0
    && formTotalTenderValue < BUYBACK_MIN_TOTAL_VALUE_USD;

  const hasPendingSelections = sortedSellRequestInputs.some(
    (input) => input && !input.selection,
  );

  // const showBulkApplyCard = sortedSellRequestInputs.length > 1;

  const submittedGroupedSellRequests = useMemo(() => {
    if (!feedback || !Array.isArray(feedback.sellRequests)) {
      return [];
    }
    return buildSellRequestInputs(feedback.sellRequests).map((input) => {
      if (input && input.selection) {
        const updated = updateInputWithSelection(input, input.selection);
        if (input.requestedUnits !== null && input.requestedUnits !== undefined) {
          updated.requestedUnits = input.requestedUnits;
        }
        return updated;
      }
      return input;
    });
  }, [feedback, buildSellRequestInputs]);

  const submittedTotalTenderValue = useMemo(() => (
    submittedGroupedSellRequests.reduce((sum, group) => {
      const quantity = normalizeUnitsValue(group.requestedUnits);
      if (quantity === null) {
        return sum;
      }
      return sum + (quantity * BUYBACK_UNIT_PRICE_USD);
    }, 0)
  ), [submittedGroupedSellRequests]);

  const requestedAtDisplay = useMemo(
    () => (feedback
      ? formatDateTime(
        feedback.requestedAt
        || feedback.updatedAt
        || feedback.createdAt,
      )
      : null),
    [feedback],
  );

  const disableSubmitButton = isSubmitting
    || sortedSellRequestInputs.length === 0
    || hasPendingSelections
    || !buybackSubmissionEnabled;

  if (unauthorizedAccess) {
    return (
      <>
        <Helmet>
          <title>{BUYBACK_CONFIG.buybackTitle}</title>
          <meta name="robots" content="noindex" />
          <link
            rel="shortcut icon"
            href={`${process.env.AVG_API_PATH}/favicon.ico`}
            sizes="16x16"
          />
        </Helmet>
        <Container className="dashboard">
          <Row>
            <Col>
              <Card className="mt-4">
                <CardBody>
                  <h1 className="mb-3">{BUYBACK_CONFIG.buybackTitle}</h1>
                  <Alert color="danger" className="mb-3">
                    You are not authorized to access this buyback portal.
                  </Alert>
                  <p className="mb-0 text-muted" style={{ lineHeight: 1.6 }}>
                    If you believe this is an error, please contact Investor Relations at
                    {' '}
                    <a href={`mailto:${BUYBACK_CONFIG.contactEmail}`}>{BUYBACK_CONFIG.contactEmail}</a>
                    {' '}
                    for assistance.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{BUYBACK_CONFIG.buybackTitle}</title>
        <meta name="robots" content="noindex" />
        <link
          rel="shortcut icon"
          href={`${process.env.AVG_API_PATH}/favicon.ico`}
          sizes="16x16"
        />
      </Helmet>
      <Container className="dashboard">
        <Row>
          <Col>
            <Card className="mb-4">
              <CardBody>
                <Row className="align-items-stretch">
                  <Col lg={8} className="mb-4 mb-lg-0">
                    <h2 className="mb-2">{BUYBACK_CONFIG.buybackTitle}</h2>
                    <p className="text-muted mb-4">
                      Manage your participation in the current buyback opportunity.
                    </p>
                    <h2 className="h6 text-uppercase text-muted mb-3">{BUYBACK_CONFIG.summaryHeading}</h2>
                    <Row className="mb-4">
                      {summaryStats.map((item) => (
                        <Col sm={6} md={4} className="mb-3" key={item.label}>
                          <div className="h-100 border rounded p-3 bg-light">
                            <small className="text-uppercase text-muted d-block mb-1" style={MIN_FONT_SIZE_STYLE}>{item.label}</small>
                            <span className="font-weight-bold h5 mb-0">{item.value}</span>
                          </div>
                        </Col>
                      ))}
                      {timelineStats.map((item) => (
                        <Col sm={6} md={4} className="mb-3" key={item.label}>
                          <div className="h-100 border rounded p-3">
                            <small className="text-uppercase text-muted d-block mb-1" style={MIN_FONT_SIZE_STYLE}>{item.label}</small>
                            <span className="font-weight-bold">{item.value}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="border rounded p-3 mb-4">
                      <h4 className="h6 text-uppercase mb-3">How to participate (3 simple steps)</h4>
                      <ol className="pl-3 mb-0" style={{ lineHeight: 1.6 }}>
                        <li className="mb-3">
                          <strong>Confirm your contact information.</strong>
                          {' '}
                          Please review the details in the section below. If anything is inaccurate, update it from your
                          {' '}
                          <a href={`${process.env.AV_VC_PATH}/account/settings`}>account settings</a>
                          {' '}
                          page or email
                          {' '}
                          <a href={`mailto:${BUYBACK_CONFIG.contactEmail}`}>{BUYBACK_CONFIG.contactEmail}</a>
                          .
                        </li>
                        <li className="mb-3">
                          <strong>Review what you own.</strong>
                          {' '}
                          Use the holdings and theoretical proceeds sections to understand your units and estimated amounts before you decide.
                        </li>
                        <li className="mb-0">
                          <strong>Choose your participation level.</strong>
                          {' '}
                          Select pass, 1x, or up to 2x. If you're invested through more than one legal entity (they will be listed below), repeat the process for each. Elections can be the same or different for the different entities.
                        </li>
                      </ol>
                    </div>
                    <p className="mb-0">
                      Questions? Please contact Investor Relations at
                      {' '}
                      <a href={`mailto:${BUYBACK_CONFIG.contactEmail}`}>{BUYBACK_CONFIG.contactEmail}</a>
                      .
                    </p>
                  </Col>
                  <Col lg={4} className="d-flex">
                    <Card className="bg-light border w-100">
                      <CardBody>
                        <h3 className="h5 mb-3">{BUYBACK_CONFIG.ceoNote.heading}</h3>
                        <p className="mb-3">
                          {BUYBACK_CONFIG.ceoNote.body}
                        </p>
                        <p className="font-italic mb-2">
                          {BUYBACK_CONFIG.ceoNote.closing}
                        </p>
                        <p className="font-weight-bold mb-4 text-right">
                          {BUYBACK_CONFIG.ceoNote.signature}
                        </p>
                        <div className="border-top pt-3">
                          <h4 className="h6 text-uppercase mb-2">Important Notes</h4>
                          <ul className="mb-0 pl-3">
                            {BUYBACK_CONFIG.importantNotes.map((note) => (
                              <li key={`important-note-${note}`}>{note}</li>
                            ))}
                          </ul>

                        </div>
                        <br />
                        <div className="border-top pt-2">
                          <p className="mb-0 mt-3" style={{ lineHeight: 1.6 }}>
                            For additional information, please see here:
                            <ul className="mt-1">
                              <li>
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href="https://3925488.fs1.hubspotusercontent-na1.net/hubfs/3925488/_SoT_Mirror/22_Investor_Engagement/2025_AV_Buy_Back/Investor%20Tender%20Offer%20Fixed%20Price%202025%20-%20FOR%20REVIEW%20ONLY.pdf"
                                >
                                  Investor Tender Offer Fixed Price 2025
                                </a>
                              </li>
                              <li>
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href="https://3925488.fs1.hubspotusercontent-na1.net/hubfs/3925488/_SoT_Mirror/22_Investor_Engagement/2025_AV_Buy_Back/AV%20-%20Historical%20Pricing%20(9-30-25).pdf"
                                >
                                  AV - Historical Pricing (9/30/25)
                                </a>
                              </li>
                              <li>
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href="https://3925488.fs1.hubspotusercontent-na1.net/hubfs/3925488/_SoT_Mirror/22_Investor_Engagement/2025_AV_Buy_Back/AV%20-%20Equity%20Flows%20(9-30-25).pdf"
                                >
                                  AV - Equity Flows (9/30/25)
                                </a>
                              </li>
                              <li>
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href="https://3925488.fs1.hubspotusercontent-na1.net/hubfs/3925488/_SoT_Mirror/22_Investor_Engagement/2025_AV_Buy_Back/2024%20Audited%20Financials.pdf"
                                >
                                  2024 Audited Financials
                                </a>
                              </li>
                            </ul>
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="mt-4">
              <CardBody>
                <h4 className="mb-3">Your Contact Information</h4>
                <div className="border rounded p-3 mb-4 bg-light">
                  <p className="mb-0" style={{ fontSize: '14px', lineHeight: 1.55 }}>
                    We will use this information for buyback communications and DocuSign paperwork. If any corrections are needed, please visit your
                    {' '}
                    <a href={`${process.env.AV_VC_PATH}/account/settings`}>account settings</a>
                    {' '}
                    or contact
                    {' '}
                    <a href={`mailto:${BUYBACK_CONFIG.contactEmail}`}>{BUYBACK_CONFIG.contactEmail}</a>
                    .
                  </p>
                </div>
                <Row>
                  <Col md={6} className="mb-3">
                    <p className="small text-uppercase text-muted mb-1" style={MIN_FONT_SIZE_STYLE}>First Name</p>
                    <p className="mb-2">{auth.user?.firstName || '—'}</p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="small text-uppercase text-muted mb-1" style={MIN_FONT_SIZE_STYLE}>Last Name</p>
                    <p className="mb-2">{auth.user?.lastName || '—'}</p>
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="mb-3">
                    <p className="small text-uppercase text-muted mb-1" style={MIN_FONT_SIZE_STYLE}>Email</p>
                    <p className="mb-2">{auth.user?.email1 || '—'}</p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="small text-uppercase text-muted mb-1" style={MIN_FONT_SIZE_STYLE}>Primary Phone</p>
                    <p className="mb-2">{userPhone || '—'}</p>
                  </Col>
                </Row>
                <Row>
                  <Col className="mb-3">
                    <p className="small text-uppercase text-muted mb-1" style={MIN_FONT_SIZE_STYLE}>Mailing Address</p>
                    <p className="mb-0">{userAddress}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="mt-4">
              <CardBody className="pb-1 pt-4 ml-0 mr-0 pl-3 pr-3">
                { !feedback && <h4 className="mb-2">Choose How Much You Want to Sell</h4> }
                {!buybackSubmissionEnabled && submissionStatusMessage && (
                  <p>
                    {submissionStatusMessage}
                    {showSubmissionWindowRange && (
                    <span className="d-block mt-1 mb-0">
                      Submission window: {submissionWindowRangeLabel}
                    </span>
                    )}
                  </p>
                )}

                {isBuybackLoading && (
                  <div className="my-4 text-center">
                    <Loader />
                  </div>
                )}

                {!isBuybackLoading && hasBuybackError && (
                  <ErrorNotification />
                )}

                {!isBuybackLoading && !hasBuybackError && !hasBuybackData && (
                  <p className="mb-0 text-muted">
                    No ownership information is available at this time.
                  </p>
                )}

                {!isBuybackLoading && !hasBuybackError && hasBuybackData && (
                  <>
                    {/* {submissionSuccessMessage && (
                    <Alert color="success">
                      {submissionSuccessMessage}
                    </Alert>
                    )} */}

                    {submitError && (
                      <Alert color="danger">
                        {getErrorMessage(submitError)}
                      </Alert>
                    )}

                    {feedbackError && (
                      <Alert color="danger">
                        {getErrorMessage(feedbackError)}
                      </Alert>
                    )}

                    {isFeedbackLoading && (
                      <div className="my-4 text-center">
                        <Loader />
                      </div>
                    )}

                    {!isFeedbackLoading && feedback && (
                      <div className="mb-4" ref={feedbackSectionRef}>
                        <div className="d-flex align-items-center text-success mb-3">
                          <CheckCircleIcon fontSize="small" className="mr-2" />
                          <h4>We have received your buyback elections.</h4>
                        </div>

                        <Card className="mt-3">
                          <CardBody className="py-3">
                            <h6 className="text-uppercase text-muted small mb-2" style={MIN_FONT_SIZE_STYLE}>Next Steps</h6>
                            <ul className="mb-2 pl-3">
                              {BUYBACK_CONFIG.nextSteps.map((item) => (
                                <li key={`next-steps-submitted-${item}`}>{item}</li>
                              ))}
                            </ul>
                            <p className="text-muted small mb-0" style={MIN_FONT_SIZE_STYLE}>
                              To request any changes, please contact Investor Relations at
                              {' '}
                              <a href={`mailto:${BUYBACK_CONFIG.contactEmail}`}>{BUYBACK_CONFIG.contactEmail}</a>
                              .
                            </p>
                          </CardBody>
                        </Card>
                        {submittedGroupedSellRequests.length > 0 ? (
                          <>
                            {submittedGroupedSellRequests.map((group) => {
                              const childLots = Array.isArray(group.childSellRequests) ? group.childSellRequests : [];
                              const hasMultipleLots = childLots.length > 1;
                              return (
                                <Card className="border mb-3" key={`submitted-${group.sellRequestId}`}>
                                  <CardBody className="py-3 d-flex flex-column">
                                    <div className="mb-5">
                                      <p className="mb-2 font-weight-bold" style={{ fontSize: '15px' }}>
                                        {group.entityName || group.sellRequestId || 'Sell request'}
                                      </p>
                                      <div className={hasMultipleLots ? 'rounded bg-light p-3' : ''}>
                                        <div className="table-responsive">
                                          <table className="table table-sm mb-0">
                                            <thead>
                                              <tr className="text-muted" style={MIN_FONT_SIZE_STYLE}>
                                                <th>Unit Class</th>
                                                <th className="text-right">Units</th>
                                                <th className="text-right">Unit Price</th>
                                                <th className="text-right">Catch-Up Price</th>
                                                <th className="text-right">Acquisition Date</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {sortLotsByAcquisitionDateAscending(childLots).map((lot, lotIndex) => {
                                                const lotUnitsValue = lot.shares
                                                      ?? lot.maximumAvailable
                                                      ?? lot.numberOfUnits;
                                                const lotKey = `${group.sellRequestId || 'group'}-${lot.sellRequestId || 'lot'}-${lotIndex}`;
                                                return (
                                                  <tr key={lotKey}>
                                                    <td>{lot.shareClass || '—'}</td>
                                                    <td className="text-right">
                                                      {lotUnitsValue !== null && lotUnitsValue !== undefined
                                                        ? formatNumberWithSeparators(lotUnitsValue)
                                                        : '—'}
                                                    </td>
                                                    <td className="text-right">
                                                      {formatCurrency(lot.sharePrice ?? group.sharePrice)}
                                                    </td>
                                                    <td className="text-right">
                                                      {lot.catchupPricePerShare !== null
                                                        && lot.catchupPricePerShare !== undefined
                                                        ? formatCurrency(lot.catchupPricePerShare)
                                                        : '—'}
                                                    </td>
                                                    <td className="text-right">
                                                      {lot.acquisitionDate ? lot.acquisitionDate : '—'}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                    <p className="text-muted small mb-2" style={MIN_FONT_SIZE_STYLE}>
                                      Participation:
                                      {' '}
                                      {getSelectionLabel(group.selection)}
                                    </p>
                                    <p className="text-muted smaller mb-2 mt-2" style={{ fontSize: '12px', lineHeight: 1.4 }}>
                                      {PRO_RATA_2X_FOOTNOTE}
                                    </p>
                                    <div className="text-muted small mb-2" style={MIN_FONT_SIZE_STYLE}>
                                      Units Sold:
                                      {' '}
                                      {group.requestedUnits !== null && group.requestedUnits !== undefined
                                        ? formatNumberWithSeparators(group.requestedUnits)
                                        : '—'}
                                    </div>
                                    {(() => {
                                      const estimatedAmount = getSelectionAmount(group, group.selection);
                                      if (estimatedAmount === null || Number.isNaN(estimatedAmount)) {
                                        return null;
                                      }
                                      return (
                                        <div className="text-muted small" style={MIN_FONT_SIZE_STYLE}>
                                          Estimated Amount:
                                          {' '}
                                          {formatCurrency(estimatedAmount)}
                                        </div>
                                      );
                                    })()}
                                  </CardBody>
                                </Card>
                              );
                            })}

                            {submittedTotalTenderValue < BUYBACK_MIN_TOTAL_VALUE_USD && (
                              <p className="text-danger small mb-0" style={MIN_FONT_SIZE_STYLE}>
                                Minimum participation is
                                {' '}
                                {minParticipationDisplay}.
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-muted mb-0">No submitted unit details available.</p>
                        )}
                      </div>
                    )}
                    {!feedback && !isFeedbackLoading && (
                      <Form onSubmit={handleSubmit}>
                        {sortedSellRequestInputs.length === 0 ? (
                          <Alert color="light" className="border mb-4">
                            No eligible ownership information is available right now.
                          </Alert>
                        ) : (
                          <>
                            <div className="mb-3">
                              {showElectionSelectors ? (
                                <>
                                  <p className="text-muted small mb-0" style={MIN_FONT_SIZE_STYLE}>
                                    We will buy eligible units at
                                    {' '}
                                    {buybackPriceDisplay}
                                    {' '}
                                    per unit. Choose the preset election that matches how you’d like us to tender units for each holding.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <h6 className="mb-1">Your positions</h6>
                                  <p className="text-muted small mb-0" style={MIN_FONT_SIZE_STYLE}>
                                    <b>The submission window is currently closed. Review your holdings below.</b>
                                  </p>
                                </>
                              )}
                            </div>
                            <Row>
                              {sortedSellRequestInputs.map((input) => {
                                const quantity = normalizeUnitsValue(input.requestedUnits);
                                const totalUnits = input.numberOfUnits
                                  ?? input.shares
                                  ?? input.maximumAvailable;
                                const entityDetails = [
                                  { label: 'Entity', value: input.entityName || '—' },
                                  totalUnits !== null && totalUnits !== undefined
                                    ? { label: 'Total Units Vested', value: formatNumberWithSeparators(totalUnits) }
                                    : null,
                                  input.proRataUnits !== null && input.proRataUnits !== undefined
                                    ? { label: 'Pro Rata Units (1×)', value: formatNumberWithSeparators(input.proRataUnits) }
                                    : null,
                                  input.proRataDollarAmount !== null && input.proRataDollarAmount !== undefined
                                    ? { label: 'Pro Rata Dollars (1×)', value: formatCurrency(input.proRataDollarAmount) }
                                    : null,
                                  input.proRataUnits2x !== null && input.proRataUnits2x !== undefined
                                    ? { label: 'Pro Rata Units (2×)', value: formatNumberWithSeparators(input.proRataUnits2x) }
                                    : null,
                                  input.proRataDollarAmount2x !== null && input.proRataDollarAmount2x !== undefined
                                    ? { label: 'Pro Rata Dollars (2×)', value: formatCurrency(input.proRataDollarAmount2x) }
                                    : null,
                                ].filter(Boolean);
                                const childLots = Array.isArray(input.childSellRequests) && input.childSellRequests.length
                                  ? input.childSellRequests
                                  : [input];
                                const hasMultipleLots = childLots.length > 1;

                                const scenarioRows = [
                                  {
                                    key: `${input.sellRequestId}-hold`,
                                    label: 'Pass (0)',
                                    amount: null,
                                    units: null,
                                    optionValue: 'HOLD',
                                  },
                                  {
                                    key: `${input.sellRequestId}-pro-rata`,
                                    label: 'Sell 1× Pro-Rata',
                                    amount: input.proRataDollarAmount,
                                    units: input.proRataUnits,
                                    optionValue: 'PRO_RATA',
                                  },
                                  {
                                    key: `${input.sellRequestId}-pro-rata-2x`,
                                    label: PRO_RATA_2X_LABEL_WITH_FOOTNOTE,
                                    amount: input.proRataDollarAmount2x,
                                    units: input.proRataUnits2x,
                                    optionValue: 'PRO_RATA_2X',
                                  },
                                ].filter((row) => (
                                  row.optionValue === 'HOLD'
                                  || (row.amount !== null && row.amount !== undefined && row.amount !== '')
                                  || (row.units !== null && row.units !== undefined && row.units !== '')
                                ));

                                return (
                                  <Col md={6} className="mb-3 d-flex" key={input.sellRequestId}>
                                    <Card className="border w-100 h-100">
                                      <CardBody className="p-3 d-flex flex-column">
                                        <div className="mb-3">
                                          <p className="mb-2 font-weight-bold" style={{ fontSize: '15px' }}>
                                            {input.entityName}
                                          </p>
                                          {/* {entityDetails.length > 0 && (
                                            <div className="d-flex flex-wrap mb-3">
                                              {entityDetails.map((chip, chipIndex) => (
                                                <div className="mr-3 mb-2" key={`${input.sellRequestId}-entity-${chip.label || chipIndex}`}>
                                                  <div className="text-uppercase text-muted small" style={MIN_FONT_SIZE_STYLE}>{chip.label}</div>
                                                  <div className="font-weight-semibold text-dark">
                                                    {chip.value ?? '—'}
                                                    {chip.unitsLabel ? (
                                                      <span className="ml-1 text-muted">
                                                        (
                                                        {formatNumberWithSeparators(chip.label.includes('2×') ? input.proRataUnits2x : input.proRataUnits)}
                                                        {' '}
                                                        units
                                                        )
                                                      </span>
                                                    ) : null}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )} */}
                                          <div className={hasMultipleLots ? 'rounded bg-light p-3' : ''}>
                                            <div className="table-responsive">
                                              <table className="table table-sm mb-0">
                                                <thead>
                                                  <tr className="text-muted" style={MIN_FONT_SIZE_STYLE}>
                                                    <th>Unit Class</th>
                                                    <th className="text-right">Vested Units</th>
                                                    <th className="text-right">Unit Price</th>
                                                    <th className="text-right">Catch-Up Price</th>
                                                    <th className="text-right">Acquisition Date</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {sortLotsByAcquisitionDateAscending(childLots).map((lot, lotIndex) => {
                                                    const lotUnitsValue = lot.shares;

                                                    const lotKey = `${input.sellRequestId || 'input'}-${lot.sellRequestId || 'lot'}-${lotIndex}`;
                                                    return (
                                                      <tr key={lotKey}>
                                                        <td>{lot.shareClass || '—'}</td>
                                                        <td className="text-right">
                                                          {lotUnitsValue !== null && lotUnitsValue !== undefined
                                                            ? formatNumberWithSeparators(lotUnitsValue)
                                                            : '—'}
                                                        </td>
                                                        <td className="text-right">
                                                          {formatCurrency(lot.sharePrice)}
                                                        </td>
                                                        <td className="text-right">
                                                          {lot.catchupPricePerShare !== null
                                                            && lot.catchupPricePerShare !== undefined
                                                            ? formatCurrency(lot.catchupPricePerShare)
                                                            : '—'}
                                                        </td>
                                                        <td className="text-right">
                                                          {lot.acquisitionDate ? lot.acquisitionDate : '—'}
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                            {hasMultipleLots && (
                                              <p className="text-muted small mb-0 mt-2" style={MIN_FONT_SIZE_STYLE}>
                                                We’ll fulfill this election using the lowest unit price holdings first.
                                              </p>
                                            )}
                                          </div>
                                        </div>

                                        {showElectionSelectors && (
                                          <div className="mb-3">
                                            <Label className="small text-muted text-uppercase mb-2" style={MIN_FONT_SIZE_STYLE}>
                                              Participation
                                            </Label>
                                            <div>
                                              {scenarioRows.map((row) => {
                                                const unitsDisplay = formatUnitsLabel(row.units);
                                                const labelWithUnits = unitsDisplay
                                                  ? `${row.label} (${unitsDisplay})`
                                                  : row.label;
                                                const amountDisplay = row.amount !== null && row.amount !== undefined && row.amount !== ''
                                                  ? formatCurrency(row.amount)
                                                  : null;
                                                return (
                                                  <FormGroup check className="mb-1" key={row.key}>
                                                    <Label check className="d-flex justify-content-between align-items-center w-100 mb-0">
                                                      <span className="d-flex align-items-center">
                                                        <Input
                                                          type="radio"
                                                          name={`buyback-selection-${input.sellRequestId}`}
                                                          value={row.optionValue}
                                                          checked={input.selection === row.optionValue}
                                                          onChange={() => handleSellRequestSelectionChange(
                                                            input.sellRequestId,
                                                            row.optionValue,
                                                          )}
                                                        />
                                                        <span className="ml-2">{labelWithUnits}</span>
                                                      </span>
                                                      {amountDisplay && (
                                                        <span className="font-weight-bold">{amountDisplay}</span>
                                                      )}
                                                    </Label>
                                                  </FormGroup>
                                                );
                                              })}
                                              <p className="text-muted smaller mb-2 mt-2" style={{ fontSize: '12px', lineHeight: 1.4 }}>
                                                {PRO_RATA_2X_FOOTNOTE}
                                              </p>
                                            </div>
                                            {validationErrors.sellRequests[input.sellRequestId] && (
                                              <FormText color="danger">
                                                {validationErrors.sellRequests[input.sellRequestId]}
                                              </FormText>
                                            )}
                                          </div>
                                        )}
                                      </CardBody>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>

                            {showElectionSelectors && (

                            <div className="mt-4">
                              <div className="text-uppercase text-muted small mb-1" style={MIN_FONT_SIZE_STYLE}>{proceedsHeading}</div>

                              { !hasProRata2xSelection && (
                              <div className="font-weight-bold h4 mt-2 mb-1">
                                {formatCurrency(formTotalSelectedAmount)}
                                {formTotalSelectedUnits > 0 && (
                                <span className="h6 ml-2 mr-2">

                                  (Selling {' '}
                                  {formatNumberWithSeparators(formTotalSelectedUnits)}
                                  {' '}
                                  units)

                                </span>
                                )}
                              </div>
                              )}

                              { hasProRata2xSelection && (
                              <div className="font-weight-bold h4 mt-2 mb-1">
                                <>Min: {formatCurrency(formTotalMinAmount)} </>

                                {formTotalSelectedUnits > 0 && (
                                <span className="h6 ml-2 mr-2">

                                  (Selling {' '}
                                  {formatNumberWithSeparators(formTotalSelectedUnits)}
                                  {' '}
                                  units){' '}

                                </span>
                                )}

                                <>Max: {formatCurrency(formTotalSelectedAmount)}</>
                              </div>
                              )}

                              <div className="text-muted small" style={MIN_FONT_SIZE_STYLE}>{proceedsDescription}</div>
                            </div>

                            )}

                            {showElectionSelectors && (
                            <>
                              <p className="mb-4">
                                This buyback is being offered on a pro-rata basis, meaning each unitholder has the right to sell a portion of their holdings based on their ownership percentage. You may also elect to sell additional units up to 2x your pro-rata amount, subject to availability. Once submitted, your election is binding. Ownership snapshot valued as of {valuationDate}.
                              </p>

                              {validationErrors.form && !isBelowMinimum && (
                              <Alert color="warning" className="mb-3">
                                {validationErrors.form}
                              </Alert>
                              )}

                              <div className="d-flex align-items-center">
                                <Button
                                  color="primary"
                                  type="submit"
                                  disabled={disableSubmitButton}
                                  style={disableSubmitButton ? {
                                    opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#7B569B', borderColor: '#7B569B', color: '#FFFFFF',
                                  } : undefined}
                                >
                                  {isSubmitting ? 'Submitting…' : 'Submit Your Elections'}
                                </Button>
                              </div>
                              <p className="text-muted small mt-2 mb-0" style={MIN_FONT_SIZE_STYLE}>
                                By submitting this form, you acknowledge that you have read and understand the terms of the buyback program.
                              </p>
                            </>
                            )}
                          </>
                        )}
                      </Form>
                    )}
                  </>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="mt-4 border">
              <CardBody>
                <h5 className="h6 text-uppercase text-muted mb-2">Legal Disclosure</h5>
                <p className="text-muted mb-0" style={{ lineHeight: 1.6 }}>
                  {BUYBACK_CONFIG.legalDisclosure}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Buyback;
